import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { authService } from "../services/authService";
import { pendoService } from "../services/pendoService";
import { UserProfile, UserRole, userService } from "../services/userService";
import { updateUserProfileWithPermissions } from "../utils/userProfileUtils";

import { useAuth } from "./AuthContext";

interface UserContextType {
  user: UserProfile | null;
  selectedRole: UserRole | null;
  setSelectedRole: (role: UserRole) => Promise<void>;
  showRoleModal: boolean;
  setShowRoleModal: (show: boolean) => void;
  practitionerRole: UserRole | null;
  careTeamRoles: UserRole[];
  openRoleSelector: () => void;
  isUserConfigurationReady: boolean;

  // Permission-related properties
  permissions: string[];
  isPerfectServeUser: boolean;
  isPermissionsLoading: boolean;
  permissionsError: string | null;
  hasPermission: (permission: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const { isAuthenticated, getToken } = useAuth();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [selectedRole, setInternalSelectedRoleState] = useState<UserRole | null>(null);
  const [showRoleModal, setInternalShowRoleModal] = useState<boolean>(false);
  const [isUserConfigurationReady, setIsUserConfigurationReady] = useState<boolean>(false);

  // Permission states
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);

  // Permission state
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);

  // Computed values based on current state
  const practitionerRole = userService.getPractitionerRole(user);
  const careTeamRoles = userService.getCareTeamRoles(user);
  const isPerfectServeUser = userService.isPerfectServeEmployee(permissions);

  const hasPermission = (permission: string): boolean => {
    // Use direct array check to avoid race conditions
    return permissions.includes(permission);
  };

  // Simplified Pendo initialization and UserConfiguration update
  const initializePendo = (user: UserProfile, role: UserRole) => {
    try {
      // Update UserConfiguration with selected role
      const userConfiguration = userService.createUserConfiguration(user, role);
      if (userConfiguration) {
        // Store globally for Angular Elements
        (window as any).PSX_USER_CONFIGURATION = userConfiguration;
      }

      // Initialize Pendo
      const pendoConfig = pendoService.createPendoConfig(user, role);
      pendoService.initialize(pendoConfig);
    } catch (_error) {
      // Silently handle initialization errors
    }
  };

  const setShowRoleModal = (show: boolean) => {
    setInternalShowRoleModal(show);
  };

  const openRoleSelector = (): void => {
    setShowRoleModal(true);
  };

  // Internal function for setting role without additional API calls
  const setSelectedRoleInternal = (role: UserRole): void => {
    setInternalSelectedRoleState(role);
  };

  const refreshPermissions = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      setPermissions([]);
      setPermissionsError(null);
      return;
    }

    const token = getToken();
    if (!token) {
      setPermissions([]);
      setPermissionsError(null);
      return;
    }

    try {
      setIsPermissionsLoading(true);
      setPermissionsError(null);

      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Permissions loading timeout")), 10000);
      });

      const loadedPermissions = (await Promise.all([userService.loadPermissions(token), timeoutPromise])) as string[];

      setPermissions(loadedPermissions);

      // Update stored user profile with new permissions
      // We'll get the current user from localStorage since we can't depend on user state here
      const currentStoredUser = userService.loadFromStorage();
      if (currentStoredUser) {
        updateUserProfileWithPermissions(currentStoredUser, loadedPermissions);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load permissions";
      setPermissionsError(errorMessage);
      setPermissions([]);
    } finally {
      setIsPermissionsLoading(false);
    }
  }, [isAuthenticated, getToken]);

  useEffect(() => {
    const loadUser = async () => {
      if (!isAuthenticated) {
        setUser(null);
        setInternalSelectedRoleState(null);
        setShowRoleModal(false);
        setIsUserConfigurationReady(false);
        setPermissions([]);
        setPermissionsError(null);
        return;
      }

      try {
        // First try to load user from localStorage
        let currentUser = userService.loadFromStorage();

        // ALWAYS fetch fresh user profile from Directory API on startup
        const token = getToken();
        if (token && token.trim() !== "") {
          // Get tokens from authService instead of directly from localStorage
          const tokens = authService.getTokens();
          if (tokens && tokens.id_token) {
            const userInfo = userService.extractUserFromIdToken(tokens.id_token);
            const identityId = userInfo.sub;

            if (identityId) {
              try {
                // Fetch fresh profile from API
                let fetchedUser = await userService.fetchProfile(identityId, token);

                // Enhance with ID token data
                fetchedUser = userService.enhanceProfileWithIdToken(fetchedUser, userInfo);

                // Store the enhanced profile
                userService.storeProfile(fetchedUser);

                currentUser = fetchedUser;
              } catch (profileError) {
                console.error("Failed to fetch user profile:", profileError);
                // If API call fails, try to use cached user data
                if (!currentUser) {
                  console.info("No cached user data available, user will need to sign in again");
                  // Clear auth state if we can't get user data
                  authService.clearAllAuthState();
                  return;
                }
              }
            }
          }
        }
        setUser(currentUser);

        // Load permissions for the user and set them in state
        if (currentUser && token) {
          // Load permissions directly to avoid circular dependency
          try {
            setIsPermissionsLoading(true);
            setPermissionsError(null);

            const loadedPermissions = await userService.loadPermissions(token);
            setPermissions(loadedPermissions);

            // Update stored user profile with new permissions
            updateUserProfileWithPermissions(currentUser, loadedPermissions);

            // CRITICAL: Update window.PSX_USER_CONFIGURATION with permissions
            // This must happen AFTER permissions are loaded so components can detect isPerfectServeUser correctly
            const userWithPermissions = { ...currentUser, permissions: loadedPermissions };
            if (userWithPermissions.roles && userWithPermissions.roles.length > 0) {
              const userConfiguration = userService.createUserConfiguration(userWithPermissions, null);
              if (userConfiguration) {
                (window as any).PSX_USER_CONFIGURATION = userConfiguration;
                setIsUserConfigurationReady(true);
              }
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to load permissions";
            setPermissionsError(errorMessage);
            setPermissions([]);
          } finally {
            setIsPermissionsLoading(false);
          }
        } else if (currentUser?.permissions) {
          // Use cached permissions if available
          setPermissions(currentUser.permissions);

          // Create UserConfiguration with cached permissions
          if (currentUser.roles && currentUser.roles.length > 0) {
            const userConfiguration = userService.createUserConfiguration(currentUser, null);
            if (userConfiguration) {
              (window as any).PSX_USER_CONFIGURATION = userConfiguration;
              setIsUserConfigurationReady(true);
            }
          }
        }

        // Handle role selection
        if (currentUser && currentUser.roles) {
          if (currentUser.roles.length === 1 && currentUser.roles[0]) {
            // Auto-select single role
            setSelectedRoleInternal(currentUser.roles[0]);
          } else if (currentUser.roles.length > 1) {
            // Always show role modal for users with multiple roles unless they have a valid selected role
            if (!selectedRole) {
              setShowRoleModal(true);
            }
          }
        }
      } catch (_error) {
        // Silently handle user loading errors
        setUser(null);
      }
    };

    loadUser();
  }, [isAuthenticated, getToken]);

  // Initialize Pendo when selectedRole changes
  useEffect(() => {
    if (isAuthenticated && user && selectedRole) {
      // CRITICAL: Use user with permissions from state, not the stale user object
      // The permissions state is updated separately, so we need to merge it
      const userWithPermissions = { ...user, permissions };
      initializePendo(userWithPermissions, selectedRole);
    }
  }, [selectedRole, isAuthenticated, user, permissions]);

  // Enhanced setSelectedRole with Pendo integration
  const setSelectedRole = async (role: UserRole): Promise<void> => {
    setInternalSelectedRoleState(role);
    // Pendo will be initialized by the selectedRole useEffect
  };

  const value: UserContextType = {
    user,
    selectedRole,
    setSelectedRole,
    showRoleModal,
    setShowRoleModal,
    practitionerRole,
    careTeamRoles,
    openRoleSelector,
    isUserConfigurationReady,

    // Permission values
    permissions,
    isPerfectServeUser,
    isPermissionsLoading,
    permissionsError,
    hasPermission,
    refreshPermissions,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
